// Advanced (raw SQL) editor for a column's lookup. The author writes a
// full SELECT that must expose two output columns named `display` and
// `data`; {form.COL} / {header.COL} placeholders bind to the current-form
// and header values at runtime (as bound parameters — never spliced into
// SQL by the server). The engine wraps the query to add search, ordering,
// a row limit, and the total count, so the author only owns the data shape
// and any parameter-driven filtering.
//
// This is the SQL counterpart to LookupFilterEditor (the declarative
// builder). PropertyPanel decides which one to render based on whether
// `lookup_sql` is set.

import { useRef } from 'react'
import { SqlBuilderButton } from './sqlBuilder'

interface Props {
  // Current SQL query (column.lookup_sql).
  value: string
  // Current-form column names — insertable as {form.X}.
  formColumns: string[]
  // Header (sql_models[0]) column names — insertable as {header.X}. Empty
  // for the header form itself (no separate header to bind against).
  headerColumns: string[]
  // The column the lookup is defined on. Offered as its own insert option
  // ({form.SELF}) so the query can filter on the value the user typed into
  // this field before clicking the lookup. Empty hides the option.
  selfColumn?: string
  onChange: (sql: string) => void
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  fontWeight: 600,
  marginBottom: 4,
  marginTop: 6,
}

const helpStyle: React.CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  marginTop: 4,
  lineHeight: 1.5,
}

const codeStyle: React.CSSProperties = { color: 'var(--color-text)' }

const insertSelectStyle: React.CSSProperties = {
  padding: '4px 8px',
  background: 'var(--color-input-bg)',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 4,
  color: 'var(--color-text)',
  fontSize: '0.8125rem',
  cursor: 'pointer',
  maxWidth: 220,
}

export default function LookupSqlEditor({
  value, formColumns, headerColumns, selfColumn, onChange,
}: Props) {
  const taRef = useRef<HTMLTextAreaElement | null>(null)
  const self = (selfColumn || '').trim()

  // Insert a placeholder at the caret (or append) so authors don't have to
  // hand-type the exact {form.X} / {header.X} token shape.
  const insert = (token: string) => {
    const ta = taRef.current
    if (!ta) { onChange(`${value || ''}${token}`); return }
    const start = ta.selectionStart ?? value.length
    const end = ta.selectionEnd ?? value.length
    const next = `${value.slice(0, start)}${token}${value.slice(end)}`
    onChange(next)
    // Restore the caret just after the inserted token on the next tick.
    requestAnimationFrame(() => {
      ta.focus()
      const pos = start + token.length
      ta.setSelectionRange(pos, pos)
    })
  }

  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <label style={{ ...labelStyle, marginTop: 0, marginBottom: 0, flex: 1 }}>Lookup SQL query</label>
        <SqlBuilderButton value={value} onChange={onChange} mode="select" title="Lookup SQL" />
      </div>
      <textarea
        ref={taRef}
        style={{
          width: '100%',
          minHeight: 96,
          padding: '6px 8px',
          background: 'var(--color-input-bg)',
          border: '1px solid var(--color-border-strong)',
          borderRadius: 4,
          color: 'var(--color-text)',
          fontSize: '0.8125rem',
          fontFamily: 'monospace',
          resize: 'vertical',
          boxSizing: 'border-box',
        }}
        value={value}
        spellCheck={false}
        placeholder={
          'SELECT state_name AS display, state_code AS data\n'
          + 'FROM states\n'
          + `WHERE state_name LIKE '%{form.${self || 'THIS_FIELD'}}%'\n`
          + '  AND country_code = {header.COUNTRY_CODE}\n'
          + 'ORDER BY state_name'
        }
        onChange={e => onChange(e.target.value)}
      />

      <div style={helpStyle}>
        The query must return two columns named{' '}
        <code style={codeStyle}>display</code> and{' '}
        <code style={codeStyle}>data</code>. Reference current-form values
        with <code style={codeStyle}>{'{form.COL}'}</code> and header values
        with <code style={codeStyle}>{'{header.COL}'}</code> — both bind as
        parameters (you can put them inside a LIKE pattern such as{' '}
        <code style={codeStyle}>{"'%{form.COL}%'"}</code> and the value is
        still bound, not spliced). Search, ordering, and row limit are added
        automatically.
        Only a single read-only <code style={codeStyle}>SELECT</code> is
        allowed; semicolons, comments, and multiple statements are rejected
        by the server.
        {self && (
          <>
            {' '}You can also reference{' '}
            <code style={codeStyle}>{`{form.${self}}`}</code> — the value the
            user typed into this field before clicking the lookup — to filter
            the list (e.g. type part of a name, then open the picker).
          </>
        )}
      </div>

      {(self || formColumns.length > 0 || headerColumns.length > 0) && (
        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* A single dropdown keeps the field list out of the way — picking
              one inserts its {form.X} / {header.X} placeholder at the caret
              and resets the select. Much tidier than a wall of chips. */}
          <select
            value=""
            style={insertSelectStyle}
            aria-label="Insert a form or header field"
            onChange={(e) => {
              const token = e.target.value
              if (token) insert(token)
              e.target.value = ''
            }}
          >
            <option value="">+ Insert field…</option>
            {self && (
              <optgroup label="This field (typed value)">
                <option value={`{form.${self}}`}>{self}</option>
              </optgroup>
            )}
            {formColumns.length > 0 && (
              <optgroup label="Form fields">
                {formColumns.map(c => (
                  <option key={`f-${c}`} value={`{form.${c}}`}>{c}</option>
                ))}
              </optgroup>
            )}
            {headerColumns.length > 0 && (
              <optgroup label="Header fields">
                {headerColumns.map(c => (
                  <option key={`h-${c}`} value={`{header.${c}}`}>{c}</option>
                ))}
              </optgroup>
            )}
          </select>
          <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
            Inserts a <code style={codeStyle}>{'{form.X}'}</code> /{' '}
            <code style={codeStyle}>{'{header.X}'}</code> placeholder at the cursor.
          </span>
        </div>
      )}
    </div>
  )
}
